import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Box,
  Form,
  SpaceBetween,
  Spinner,
  TextContent,
  WizardProps,
} from "@amzn/awsui-components-react/polaris";
import { RootState } from "../../redux/store";
import * as ClaimValidation from "../../forms/IPL/Step1/ClaimValidation";
import * as ProductInformation from "../../forms/IPL/Step2/ProductInformation";
import * as ClaimInformation from "../../forms/IPL/Step3/ClaimInformation";
import * as AdditionalInformation from "../../forms/IPL/Step4/AdditionalInformation";
import * as FileAttachments from "../../forms/IPL/Step5/FileAttachments";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { resetAppState, setLineOfBusiness } from "../appSlice";
import { useAppSelector } from "src/redux/hooks";
import { resetFormsState, resetUpdatedForm } from "src/forms/formsSlice";
import WizardWrapper from "../../components/wizard/WizardWrapper";
import React from "react";
import ResetFormModal from "../../components/reset-form-modal/ResetFormModal";
import { LineOfBusiness, ResetFormModalContents } from "../types";
import { IPL_STEP_NAMES, FORM_VALIDATIONS } from "./constants";
import { resetIPLAppState, createIPLClaim } from "./iplAppSlice";
import ClaimSubmitted from "../../views/ClaimSubmitted";
import { FORM_PAGE_ERROR_MESSAGE } from "../constants";
import { resetWizardState } from "../../components/wizard/wizardSlice";
import FormContent from "../../forms/FormContent";
import { isDevEnvironment } from "../../environmentInfo/EnvironmentInfo";
import {
  CLAIM_VALIDATION_CONSTANTS,
  PRODUCT_INFORMATION_CONSTANTS,
  CLAIM_INFORMATION_CONSTANTS,
  ADDITIONAL_INFORMATION_CONSTANTS,
  FILE_ATTACHMENTS_CONSTANTS,
  IPL_TPA_ZURICH,
  IPL_TPA_SEDGWICK,
} from "../../forms/IPL/constants";

import { createIPLClaimModelBuilder } from "./IPLClaimModelBuilder";
import { FormStateType } from "src/forms/types";
import { REVIEW_PAGE_CONSTANTS } from "../../forms/constants";
import ReviewPage from "../../forms/reviewPage/ReviewPage";
import { cloneDeep } from "lodash";
import { CreateCargoClaimCommandInput } from "@amzn/ttechclaimintakeservice-client";
import { MetricsPublisher } from "src/metrics";
import { ClaimSubmittedStatus } from "../types";
import {
  CLIENT_ERROR_RESPONSES,
  SERVER_ERROR_RESPONSES,
  HttpStatusCodes,
} from "src/api/types";

// Deep clone form configs to prevent mutations across component instances
const DEFAULT_FORM_CONFIGS = {
  [CLAIM_VALIDATION_CONSTANTS.FORM_NAME]: cloneDeep(ClaimValidation.formConfig),
  [PRODUCT_INFORMATION_CONSTANTS.FORM_NAME]: cloneDeep(
    ProductInformation.formConfig
  ),
  [CLAIM_INFORMATION_CONSTANTS.FORM_NAME]: cloneDeep(
    ClaimInformation.formConfig
  ),
  [ADDITIONAL_INFORMATION_CONSTANTS.FORM_NAME]: cloneDeep(
    AdditionalInformation.formConfig
  ),
  [FILE_ATTACHMENTS_CONSTANTS.FORM_NAME]: cloneDeep(FileAttachments.formConfig),
};

function IPLClaimIntakeApp() {
  const wizardKey = useRef(0);
  const iplMetricsPublisherRef = useRef<MetricsPublisher | null>(null);
  const iplErrorCodeMetricsPublisherRef = useRef<MetricsPublisher | null>(null);
  const submitStartTimeRef = useRef<number>(0);
  const [submitClicked, setSubmitClicked] = useState(false);
  const [formattedFormErrors, setFormattedFormErrors] = useState(null);
  const [resetFormModalOpen, setResetFormModalOpen] = useState(false);
  const [resetFormModalContents, setResetFormModalContents] =
    useState<ResetFormModalContents>({
      title: "",
      description: null,
      confirm: "",
      cancel: "",
    });
  const [currentStep, setCurrentStep] = useState(0);
  const [renderSubmitScreen, setRenderSubmitScreen] = useState(false);
  const [stepsToRender, setStepsToRender] = useState<WizardProps.Step[]>([]);
  const [claimModel] = useState<CreateCargoClaimCommandInput | null>(null);
  const dispatch = useDispatch<AppDispatch>();

  const { goingBack } = useAppSelector((state: RootState) => state.wizard);
  const { validating, errors, allFormStates } = useAppSelector(
    (state: RootState) => state.forms
  );
  const { claimSubmitSuccess, loadingIPLClaim } = useAppSelector(
    (state: RootState) => state.app
  );
  const { user } = useAppSelector((state: RootState) => state.auth);
  const {
    amazonClaimReferenceId,
    submitScreenStatus,
    createIPLClaimResponse,
    iplTpa,
  } = useAppSelector((state: RootState) => state.iplApp);

  // IPL steps - no longer stage-based
  const IPL_STEPS: WizardProps.Step[] = [
    {
      title: CLAIM_VALIDATION_CONSTANTS.FORM_HEADER,
      description: CLAIM_VALIDATION_CONSTANTS.FORM_DESCRIPTION,
      content: (
        <Form errorText={formattedFormErrors} errorIconAriaLabel={"Error"}>
          <FormContent
            formName={ClaimValidation.formName}
            formConfig={ClaimValidation.formConfig}
            formValidation={ClaimValidation.formValidation}
            data-testid="claim-validation-form"
            key="claim-validation-form"
          />
        </Form>
      ),
      isOptional: isDevEnvironment(),
    },
    {
      title: PRODUCT_INFORMATION_CONSTANTS.FORM_HEADER,
      description: PRODUCT_INFORMATION_CONSTANTS.FORM_DESCRIPTION,
      content: (
        <Form errorText={formattedFormErrors} errorIconAriaLabel={"Error"}>
          <FormContent
            formName={ProductInformation.formName}
            formConfig={ProductInformation.formConfig}
            formValidation={ProductInformation.formValidation}
            data-testid="product-information-form"
            key="product-information-form"
          />
        </Form>
      ),
      isOptional: isDevEnvironment(),
    },
    {
      title: CLAIM_INFORMATION_CONSTANTS.FORM_HEADER,
      description: CLAIM_INFORMATION_CONSTANTS.FORM_DESCRIPTION,
      content: (
        <Form errorText={formattedFormErrors} errorIconAriaLabel={"Error"}>
          <FormContent
            formName={ClaimInformation.formName}
            formConfig={ClaimInformation.formConfig}
            formValidation={ClaimInformation.formValidation}
            data-testid="claim-information-form"
            key="claim-information-form"
          />
        </Form>
      ),
      isOptional: isDevEnvironment(),
    },
    {
      title: ADDITIONAL_INFORMATION_CONSTANTS.FORM_HEADER,
      description: ADDITIONAL_INFORMATION_CONSTANTS.FORM_DESCRIPTION,
      content: (
        <Form errorText={formattedFormErrors} errorIconAriaLabel={"Error"}>
          <FormContent
            formName={AdditionalInformation.formName}
            formConfig={AdditionalInformation.formConfig}
            formValidation={AdditionalInformation.formValidation}
            data-testid="additional-information-form"
            key="additional-information-form"
          />
        </Form>
      ),
      isOptional: isDevEnvironment(),
    },
    {
      title: FILE_ATTACHMENTS_CONSTANTS.FORM_HEADER,
      description: FILE_ATTACHMENTS_CONSTANTS.FORM_DESCRIPTION,
      content: (
        <Form errorText={formattedFormErrors} errorIconAriaLabel={"Error"}>
          <FormContent
            formName={FILE_ATTACHMENTS_CONSTANTS.FORM_NAME}
            formConfig={FileAttachments.formConfig}
            formValidation={FileAttachments.formValidation}
            data-testid="file-attachments-form"
            key="file-attachments-form"
          />
        </Form>
      ),
      isOptional: isDevEnvironment(),
    },
    {
      title: REVIEW_PAGE_CONSTANTS.FORM_NAME,
      description: REVIEW_PAGE_CONSTANTS.FORM_DESCRIPTION,
      content: <ReviewPage allFormConfigs={DEFAULT_FORM_CONFIGS} />,
      isOptional: isDevEnvironment(),
    },
  ];

  const onStartNewClaim = () => {
    dispatch(resetIPLAppState());
    dispatch(resetFormsState());
    dispatch(resetWizardState());
    dispatch(resetAppState());
    dispatch(setLineOfBusiness(LineOfBusiness.IPL));
    setFormattedFormErrors(null);
    setRenderSubmitScreen(false);
    wizardKey.current += 1;
  };

  const onPrevious = () => {
    if (formattedFormErrors) setFormattedFormErrors(null);
  };

  const onNext = () => {
    // Handle next step logic for IPL
  };

  const onSkipTo = (goingBack: boolean) => {
    if (goingBack) {
      if (formattedFormErrors) setFormattedFormErrors(null);
    }
  };

  const onBackToEdit = () => {
    setCurrentStep(IPL_STEPS.length - 2);
    setRenderSubmitScreen(false);
  };

  const onResetForm = () => {
    const description = (
      <TextContent>
        <Box textAlign="left">
          <div className="modal-text">
            <div>Are you sure you want to reset the form?</div>
            <div>
              All progress will be lost and all of the form information will be
              deleted.
            </div>
          </div>
        </Box>
      </TextContent>
    );
    setResetFormModalContents({
      title: "Reset Form",
      description,
      cancel: "Cancel",
      confirm: "Reset Form",
    });
    setResetFormModalOpen(true);
  };

  const onSubmit = () => {
    setSubmitClicked(true);
  };

  const submitForm = () => {
    // Build the claim model from form states
    // Merge all form states similar to WC implementation
    let formState: FormStateType = {};
    for (const formName in allFormStates) {
      formState = { ...formState, ...allFormStates[formName] };
    }

    const additionalFields = { user, amazonClaimReferenceId, iplTpa };
    const createIPLClaimInput = createIPLClaimModelBuilder(
      formState,
      additionalFields
    );

    // Dispatch the create IPL claim action
    submitStartTimeRef.current = Date.now();
    dispatch(createIPLClaim({ createIPLClaimInput }));

    setFormattedFormErrors(null);
    setSubmitClicked(false);
    setRenderSubmitScreen(true);
  };

  useLayoutEffect(() => {
    setStepsToRender(IPL_STEPS);
  }, []);

  useLayoutEffect(() => {
    if (validating) {
      return;
    }
    if (!errors.length && submitClicked) {
      submitForm();
      return;
    }
    setSubmitClicked(false);
    if (errors.length) {
      const errorText: any = (
        <div role="alert" title="form-errors" tabIndex={-1}>
          <TextContent>
            <span className="form-page-error">{FORM_PAGE_ERROR_MESSAGE}</span>
          </TextContent>
        </div>
      );
      if (!goingBack) {
        dispatch(resetUpdatedForm());
        setFormattedFormErrors(errorText);
      }
    }
  }, [errors, validating, submitClicked]);

  // Initialize metrics publishers and publish form visited on mount
  useEffect(() => {
    iplMetricsPublisherRef.current = new MetricsPublisher("iplClaimIntake");
    iplErrorCodeMetricsPublisherRef.current = new MetricsPublisher(
      "iplClaimSubmittedErrorCode"
    );
    iplMetricsPublisherRef.current.publishIPLClaimFormVisited();
  }, []);

  // Publish metrics based on submission outcome
  useEffect(() => {
    if (submitScreenStatus === ClaimSubmittedStatus.SUCCESS) {
      iplMetricsPublisherRef.current?.publishIPLClaimSubmittedSuccessMetrics();

      // Publish TPA-specific metric based on the assigned TPA
      if (iplTpa === IPL_TPA_SEDGWICK) {
        iplMetricsPublisherRef.current?.publishIPLClaimSubmittedSuccessSedgwickMetrics();
      } else if (iplTpa === IPL_TPA_ZURICH) {
        // Zurich TPA is not yet implemented; metric is pre-wired for future use
        iplMetricsPublisherRef.current?.publishIPLClaimSubmittedSuccessZurichMetrics();
      }

      iplMetricsPublisherRef.current?.publishIPLSubmitClaimLatencyMetrics(
        Date.now() - submitStartTimeRef.current
      );

      const country =
        allFormStates[CLAIM_INFORMATION_CONSTANTS.FORM_NAME]?.[
          CLAIM_INFORMATION_CONSTANTS.COUNTRY_WHERE_INCIDENT_OCCURRED
        ]?.value;
      if (country) {
        iplMetricsPublisherRef.current?.publishIPLClaimSubmittedByCountry(
          country
        );
      }
    }

    if (submitScreenStatus === ClaimSubmittedStatus.ERROR) {
      iplMetricsPublisherRef.current?.publishIPLClaimSubmittedFailure();

      const statusCode = createIPLClaimResponse?.statusCode;
      if (statusCode) {
        if (SERVER_ERROR_RESPONSES.includes(statusCode as HttpStatusCodes)) {
          iplMetricsPublisherRef.current?.publishIPLClaimSubmittedServerFailureMetrics();
        } else if (
          CLIENT_ERROR_RESPONSES.includes(statusCode as HttpStatusCodes)
        ) {
          iplMetricsPublisherRef.current?.publishIPLClaimSubmittedClientErrorMetrics();
        } else if (statusCode === HttpStatusCodes.REQUEST_TIMEOUT) {
          iplMetricsPublisherRef.current?.publishIPLClaimSubmittedTimeoutMetrics();
        }
        iplErrorCodeMetricsPublisherRef.current?.publishIPLClaimSubmittedErrorCodeMetrics(
          statusCode
        );
      }
    }
  }, [submitScreenStatus]);

  if (!stepsToRender?.length || loadingIPLClaim) {
    return (
      <Box textAlign="center">
        <SpaceBetween direction="vertical" size="m">
          <Spinner data-testid="ipl-claim-spinner" size="large" />
          <Box textAlign="center" variant="p">
            Loading Claim Details...
          </Box>
        </SpaceBetween>
      </Box>
    );
  }

  if (renderSubmitScreen) {
    return (
      <ClaimSubmitted
        key="claim submitted"
        onBackToEdit={onBackToEdit}
        onStartNewClaim={onStartNewClaim}
        claimModel={claimModel}
      />
    );
  }

  if (claimSubmitSuccess && renderSubmitScreen === false) {
    onStartNewClaim();
  }

  return (
    <React.Fragment>
      <ResetFormModal
        setModalOpen={setResetFormModalOpen}
        onResetForm={onStartNewClaim}
        isModalOpen={resetFormModalOpen}
        modalTitle={resetFormModalContents.title}
        modalDescription={resetFormModalContents.description}
        cancelButtonText={resetFormModalContents.cancel}
        confirmButtonText={resetFormModalContents.confirm}
      />
      <WizardWrapper
        onNext={onNext}
        onSubmit={onSubmit}
        onSkipTo={onSkipTo}
        onPrevious={onPrevious}
        onReset={onResetForm}
        currentStep={currentStep}
        stepNames={IPL_STEP_NAMES}
        allFormValidations={FORM_VALIDATIONS}
        steps={stepsToRender}
        key={wizardKey.current}
      />
    </React.Fragment>
  );
}

export default IPLClaimIntakeApp;
